import React, { FC, memo } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { BASE_FONT_SIZE, PDF_PRIMARY_COLOR } from '../pdfStyles';
import { PdfBoxDataType } from '../Vouchers/voucherHelpers';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';

type Props = {
    pdfTotalBoxData: PdfBoxDataType;
    /** When true, render every row in the same style (no border/highlight
     *  on the last row). Useful for "summary" boxes that don't have a
     *  meaningful "total" line — e.g. the rooming list's
     *  Total Passengers + Total Rooms summary. */
    flat?: boolean;
    /** Side to align the box on the page. Default 'right'. */
    align?: 'left' | 'right';
    /** When true, render the box as a card (light background + border +
     *  rounded corners). Pairs nicely with `flat` for summary boxes. */
    card?: boolean;
    /** When true, the box stretches to the parent's full width (instead of
     *  hugging its content). Useful for full-width summary cards under
     *  full-width tables. Defaults to false. */
    fullWidth?: boolean;
};

const PdfTotalBox: FC<Props> = (props: Props) => {
    const { pdfTotalBoxData, flat, align = 'right', card, fullWidth } = props;
    if (!pdfTotalBoxData?.length) return null;

    const lastIdx = pdfTotalBoxData.length - 1;
    const justifyContent = align === 'left' ? 'flex-start' : 'flex-end';

    return (
        <View style={{ flexDirection: 'row', justifyContent, margin: '0 10', marginTop: 16, marginBottom: 16 }} wrap={false}>
            <View style={{
                ...(fullWidth ? { width: '100%' } : { minWidth: 240 }),
                ...(card ? {
                    backgroundColor: '#fafbfd',
                    border: '1px solid #d8dde5',
                    borderLeft: `3px solid ${PDF_PRIMARY_COLOR}`,
                    borderRadius: 4,
                    padding: '8px 14px'
                } : {})
            }}>
                {pdfTotalBoxData.map((line, idx) => {
                    const isTotal = !flat && idx === lastIdx;
                    // Integers (counts like "3 passengers", "2 rooms") render
                    // without decimals; non-integer numbers keep 2 decimals.
                    const value = typeof line.value === 'number'
                        ? (Number.isInteger(line.value)
                            ? String(line.value)
                            : taytanFormatNumberToString(line.value))
                        : line.value;

                    const labelFontSize = card ? BASE_FONT_SIZE + 1 : BASE_FONT_SIZE;
                    const valueFontSize = card ? BASE_FONT_SIZE + 1 : BASE_FONT_SIZE;

                    return (
                        <View
                            key={line.label}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                paddingVertical: card ? 4 : 2,
                                paddingHorizontal: card ? 0 : 8,
                                ...(isTotal ? {
                                    borderTop: `1px solid ${PDF_PRIMARY_COLOR}`,
                                    marginTop: 2,
                                    paddingTop: 4
                                } : {})
                            }}
                        >
                            <Text style={{
                                fontSize: labelFontSize,
                                color: card ? '#5a6478' : (isTotal ? PDF_PRIMARY_COLOR : '#555'),
                                fontWeight: card ? 'bold' : (isTotal ? 'bold' : 'normal'),
                                letterSpacing: card ? 0.3 : 0,
                                marginRight: 16
                            }}>
                                {line.label}
                            </Text>
                            <Text style={{
                                fontSize: valueFontSize,
                                color: card ? PDF_PRIMARY_COLOR : '#222',
                                fontWeight: 'bold',
                                textAlign: 'right'
                            }}>
                                {value}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

export default memo(PdfTotalBox);
